import { useEffect, useState } from 'react';
import { getContents } from '@/services/platform/data/cms';
import {
  SectionContentResponseModelContentType,
  type IApiContentResponseModel,
  type SectionContentResponseModel,
} from '@/services/platform/api/model';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core/stores';

let cachedSlugs: string[] | undefined;
let inflight: Promise<string[]> | undefined;

async function fetchShopSectionSlugs(
  repository: IApiContentResponseModel,
  locale: string
): Promise<string[]> {
  if (cachedSlugs) return cachedSlugs;
  if (inflight) return inflight;

  inflight = (async () => {
    const result = await getContents(
      {
        fetch: `children:${repository.id}`,
        filter: [`contentType:${SectionContentResponseModelContentType.section}`],
        skip: 0,
        take: 50,
      },
      locale
    );

    const slugs = (result?.items ?? [])
      .map(item => {
        const slug = (item as SectionContentResponseModel).properties?.slug?.trim();
        return slug?.replace(/^gender_/, '');
      })
      .filter((slug): slug is string => Boolean(slug));

    cachedSlugs = slugs;
    inflight = undefined;
    return slugs;
  })();

  return inflight;
}

/**
 * Returns the list of valid shop section slugs as configured in the CMS.
 *
 * Source of truth: `appSettings.properties.shopSectionsRepository` — the
 * `Section` children are fetched once per app session and cached.
 */
export function useShopSections(): { slugs: string[]; isLoading: boolean } {
  const { appSettings } = useApp();
  const locale = useLocaleStore(state => state.locale);
  const repository = appSettings?.properties?.shopSectionsRepository;

  const [slugs, setSlugs] = useState<string[]>(cachedSlugs ?? []);
  const [isLoading, setIsLoading] = useState(!cachedSlugs && Boolean(repository));

  useEffect(() => {
    if (!repository || cachedSlugs) {
      setSlugs(cachedSlugs ?? []);
      setIsLoading(false);
      return;
    }

    let active = true;
    setIsLoading(true);
    fetchShopSectionSlugs(repository, locale)
      .then(result => {
        if (!active) return;
        setSlugs(result);
        setIsLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [repository, locale]);

  return { slugs, isLoading };
}

/**
 * Extracts the shop section slug from a URL path and validates it against the
 * list of slugs known to the CMS.
 *
 * Path layouts handled:
 *  - `{lang}/shop/{section}` → returns `{section}` when valid
 *  - `{lang}/shop/{section}/...` → returns `{section}` when valid
 *
 * Returns `undefined` when no section segment is present or the slug is not in
 * `validSlugs`.
 */
export function getSectionFromPath(fullPath: string | undefined, validSlugs: string[]): string | undefined {
  if (!fullPath) return undefined;
  const parts = fullPath.split('/').filter(Boolean);
  const shopIdx = parts.indexOf('shop');
  if (shopIdx === -1 || shopIdx >= parts.length - 1) return undefined;
  const candidate = parts[shopIdx + 1];
  const normalizedCandidate = candidate.replace(/^gender_/, '');
  return validSlugs.includes(normalizedCandidate) ? normalizedCandidate : undefined;
}
